import { Typography, Box, Divider } from "@mui/material";
import { Description as DocIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import { useToast } from "../components/common/custom-component/ToastProvider";
import { useDispatch, useSelector } from "react-redux";

import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import Loader from "../components/common/custom-component/Loader";
import { logger } from "helpers/logger";

import { manageTemplatesReducer, initialState } from "../reducers/manageTemplatesReducer";
import { useTableColumns } from "../hooks/useTableColumns";

import { fetchTemplatesRequest } from "../store/slices/database/templatesSlice";

import { addTemplateThunk, deleteTemplateThunk, downloadTemplateThunk } from "../store/thunks/mdsThunks";

export default function ManageTemplatesPage() {
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const { success: ToastSuccess, error: ToastError} = useToast();

    const { data: templatesData, loading, error } = useSelector((state) => ({
        data: state.templates?.data?.data,
        loading: state.templates?.loading,
        error: state.templates?.error
    }));

    const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());

    const [state, dispatchState] = useReducer(manageTemplatesReducer, initialState);

    const activeThunksRef = useRef([]);

    const displayData = useMemo(
        () => templatesData?.filter((r) => !deletedRecordIds.has(r.templateId)) || [],
        [templatesData, deletedRecordIds]
    );

    useEffect(() => {
        dispatch(fetchTemplatesRequest());
    }, [dispatch]);

    useEffect(() => {
        return () => {
            activeThunksRef.current.forEach((thunk) => {
                if (thunk && typeof thunk.abort === "function") thunk.abort();
            });
            activeThunksRef.current = [];
        };
    }, []);

    // #region SEARCH & PAGINATION
    const { setSearchQuery, filteredData } = useSearchFilter(displayData, ["templateName", "templateId", "extension"], null);

    const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

    const pagedData = useMemo(() => {
        const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
        const end = start + state.table.recordsPerPage;
        return (filteredData || []).slice(start, end);
    }, [filteredData, state.table.currentPage, state.table.recordsPerPage]);
    // #endregion

    // #region HELPER FUNCTION
    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            if (!file) return resolve({ base64: "", name: "", extension: "" });

            const ext = (file.name || "").split(".").pop()?.toLowerCase() || "";
            const reader = new FileReader();
            reader.onload = () => {
                const res = reader.result || "";
                const base64 = typeof res === "string" ? res.split(",")[1] : "";
                resolve({ base64, name: file.name, extension: ext });
            };
            reader.onerror = (e) => reject(e);
            reader.readAsDataURL(file);
        });

    const downloadBase64AsFile = (base64, fileName) => {
        const byteCharacters = atob(base64 || "");
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/octet-stream" });
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };
    // #endregion

    // #region ADD TEMPLATE
    const handleAddClick = () => {
        dispatchState({ type: "OPEN_ADD_DIALOG" });
    };

    const handleCancelAdd = useCallback(() => {
        dispatchState({ type: "CLOSE_ADD_DIALOG" });
    }, []);

    const handleConfirmAdd = useCallback(
        async (formData) => {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            try {
                const { base64, extension } = await fileToBase64(formData?.templateFile);

                if (!formData?.templateName || !base64 || !extension) {
                    ToastError(t("All fields are required"));
                    dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
                    return;
                }

                const thunkPromise = dispatch(
                    addTemplateThunk({
                        templateName: formData.templateName,
                        templateFileExtension: extension,
                        templateFile: base64,
                    })
                );

                activeThunksRef.current.push(thunkPromise);

                const result = await thunkPromise;

                activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

                if (addTemplateThunk.fulfilled.match(result)) {
                    dispatch(fetchTemplatesRequest());
                    dispatchState({ type: "CLOSE_ADD_DIALOG" });
                    ToastSuccess(t("Success"));
                } else if (addTemplateThunk.rejected.match(result)) {
                    const msg = result.payload || t("An error occured, please try again later.");
                    ToastError(msg);
                }
            } catch (e) {
                logger.error("Unexpected error in handleConfirmAdd:", e);
                ToastError(t("An unexpected error occurred"));
            } finally {
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        },
        [dispatch, ToastSuccess, ToastError, t]
    );
    // #endregion

    // #region DELETE TEMPLATE
    const handleDeleteClick = useCallback((row) => {
        dispatchState({ type: "OPEN_DELETE_DIALOG", payload: row });
    }, []);

    const handleCancelDelete = useCallback(() => {
        dispatchState({ type: "CLOSE_DELETE_DIALOG" });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!state.dialogs.selectedRecord) return;

        dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

        try {
            const templateId = state.dialogs.selectedRecord.templateId;
            const extension = state.dialogs.selectedRecord.extension;
            const templateIdWithExt = `${templateId}.${extension}`;

            const thunkPromise = dispatch(
                deleteTemplateThunk({
                    processedTemplateId: templateIdWithExt,
                })
            );

            activeThunksRef.current.push(thunkPromise);

            const result = await thunkPromise;

            activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

            if (deleteTemplateThunk.fulfilled.match(result)) {
                setDeletedRecordIds((prev) => new Set(prev).add(templateId));

                dispatch(fetchTemplatesRequest());
                dispatchState({ type: "CLOSE_DELETE_DIALOG" });
                ToastSuccess(t("Success"));
            } else if (deleteTemplateThunk.rejected.match(result)) {
                const msg = result.payload || t("An error occured, please try again later.");
                ToastError(msg);
            }
        } catch (e) {
            logger.error("Unexpected error in handleConfirmDelete:", e);
            ToastError(t("An unexpected error occurred"));
        } finally {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
        }
    }, [dispatch, ToastSuccess, ToastError, state.dialogs.selectedRecord, t]);
    // #endregion

    // #region DOWNLOAD TEMPLATE
    const handleDownloadClick = useCallback(
        async (row) => {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            try {
                const templateIdWithExt = `${row.templateId}.${row.extension}`;

                const thunkPromise = dispatch(
                    downloadTemplateThunk({
                        templateId: templateIdWithExt,
                    })
                );

                activeThunksRef.current.push(thunkPromise);

                const result = await thunkPromise;

                activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

                if (downloadTemplateThunk.fulfilled.match(result)) {
                    const base64 = result.payload?.data || "";
                    const fileName = `${row.templateName}.${row.extension}`;
                    downloadBase64AsFile(base64, fileName);
                } else if (downloadTemplateThunk.rejected.match(result)) {
                    const msg = result.payload || t("An error occured, please try again later.");
                    ToastError(msg);
                }
            } catch (e) {
                logger.error("Unexpected error in handleDownloadClick:", e);
                ToastError(t("An unexpected error occurred"));
            } finally {
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        },
        [dispatch, ToastError, t]
    );
    // #endregion

    // #region MANAGE TEMPLATES TABLE COLUMNS
    const actions = useMemo(() => ({
        onDownload: handleDownloadClick,
        onDelete: handleDeleteClick,
    }), [handleDownloadClick, handleDeleteClick]);

    const { columns, isLoading: columnsLoading } = useTableColumns('ManageTemplates', { actions });
    // #endregion

    // #region RETURN  COMPONENT
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom color="primary">
                Manage Templates
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                {t("This is a Manage Templates page.")}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {loading && !templatesData?.length ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                    <Loader variant="dots" message="Loading..." />
                </Box>
            ) : !templatesData || templatesData.length === 0 ? (
                <DataContainer
                    data={[]}
                    columns={columns} columnsLoading={columnsLoading}
                    emptyStateIcon={DocIcon}
                    emptyStateTitle={t("No templates found")}
                    emptyStateSubtitle={t("Start adding templates")}
                />
            ) : (
                <>
                    <Box sx={{ p: 2 }}>
                        <Box
                            sx={{
                                my: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <TableActionGroup sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <TableActionButton text={t("Add New Template")} variant="contained" onClick={() => handleAddClick()} />
                            </TableActionGroup>

                            <SearchFilterBar
                                mode="frontend"
                                searchValue={state.table.searchInput}
                                onSearchChange={(v) => {
                                    dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
                                    setSearchQuery(v);
                                }}
                                showFilterButton={false}
                                showSearchButton={false}
                                searchPlaceholder={t("Search templates...")}
                            />
                        </Box>

                        <DataContainer data={pagedData} columns={columns} columnsLoading={columnsLoading} showRowNumbers maxHeight={600} stickyHeader />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CustomPagination
                            currentPage={state.table.currentPage}
                            totalPages={totalPages}
                            onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </>
            )}

            <EnhancedConfirmationDialog
                open={state.dialogs.deleteConfirm}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("Confirm Delete")}
                message={t("Are you sure you want to remove this template? This action cannot be undone.")}
                confirmText={t("DELETE")}
                confirmColor="error"
                cancelText={t("Cancel")}
            />

            <EnhancedConfirmationDialog
                open={state.dialogs.addConfirm}
                onClose={handleCancelAdd}
                onConfirm={handleConfirmAdd}
                title={t("Add New Template")}
                cancelText={t("Cancel")}
                confirmText={t("Confirm")}
                confirmColor="primary"
                showMessage={false}
                defaultValues={state.forms.addTemplateForm}
                fields={[
                    {
                        name: "templateName",
                        label: t("Template Name"),
                        type: "text",
                        required: true,
                        placeholder: t("Template Name"),
                    },
                    {
                        name: "templateFile",
                        label: t("Upload Template"),
                        type: "file",
                        required: true,
                        accept: ".docx,.odt",
                        maxSizeBytes: 5242880,
                        helperText: t("Allowed: .docx, .odt (max 5MB)"),
                        dropText: t("Drag & drop your template here, or click to browse"),
                        buttonText: t("Browse"),
                    },
                ]}
            />

            <LoadingBackdrop open={state.loading.isLoadingBackdrop} message={t("Loading data, please wait...")} variant="bars" color="greenGradient" />
        </Box>
    );
    // #endregion
}
